import AccessControl from '@/components/access-control/AccessControl.component';
import { ReEnrollmentFull } from '@/components/icon/ReEnrollmentFull';
import { ReEnrollmentNone } from '@/components/icon/ReEnrollmentNone';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { ProgramChange } from '@/services/ops/investments/Program.service';
import { PlanService } from '@/services/Plan.service';
import formatters from '@/utils/Formatters';
import { ArrowForward } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isAfter, startOfDay, startOfToday } from 'date-fns';
import { FC, useCallback } from 'react';

import { ReEnrollmentType } from './ReEnrollmentModal.component';

const StyledBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(0.5),
  height: '100%',
  padding: theme.spacing(1)
}));

type ReEnrollmentConfirmModalProps = {
  isModalOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentProgramName?: string;
  currentProgramId?: number;
  newProgramName: string;
  newProgramId: number;
  planName: string;
  planId: number;
  effectiveDate: string;
  effectiveDateNote: string;
  reEnrollmentType: ReEnrollmentType;
  isForApprove?: boolean;
  programChangeId?: number;
  status?: ProgramChange['status'];
  isAnotherApprover?: boolean;
  isLoading?: boolean;
  onForApprovalSubmit?: () => void;
};

export const ReEnrollmentConfirmModal: FC<
  ReEnrollmentConfirmModalProps
> = props => {
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const approveOrRejectProgramChange = useMutation(
    [
      'PlanService.putApproveOrRejectProgramChange',
      props.planId,
      props.programChangeId
    ],
    (approval: boolean) =>
      PlanService.putApproveOrRejectProgramChange(
        props.planId,
        props.programChangeId,
        approval
      ),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: 'Error saving data',
          severity: 'error'
        });
      },
      onSettled: props.onClose,
      onSuccess: () => {
        props.onSuccess();
        snackbar.showSnackbar({
          message: 'Your data has been saved',
          severity: 'success'
        });
        queryClient.refetchQueries([
          'ProgramService.getProgramChangesWithApprovals',
          props.newProgramId
        ]);
      }
    }
  );

  const cancelProgramChange = useMutation(
    [
      'PlanService.postCancelProgramChangeApproval',
      props.planId,
      props.programChangeId
    ],
    () =>
      PlanService.postCancelProgramChangeApproval(
        props.planId,
        props.programChangeId
      ),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: 'Error saving data',
          severity: 'error'
        });
      },
      onSettled: props.onClose,
      onSuccess: () => {
        props.onSuccess();
        snackbar.showSnackbar({
          message: 'Your data has been saved',
          severity: 'success'
        });
        queryClient.refetchQueries([
          'ProgramService.getProgramChangesWithApprovals',
          props.newProgramId
        ]);
      }
    }
  );

  const onClose = useCallback(() => {
    if (
      props.isLoading ||
      cancelProgramChange.isLoading ||
      approveOrRejectProgramChange.isLoading
    )
      return;

    props.onClose();
  }, [props.isLoading]);

  const onRejectSubmit = useCallback(async () => {
    approveOrRejectProgramChange.mutateAsync(false);
  }, []);

  const onApproveSubmit = useCallback(async () => {
    approveOrRejectProgramChange.mutateAsync(true);
  }, []);

  const onCancelSubmit = useCallback(async () => {
    cancelProgramChange.mutateAsync();
  }, []);

  return (
    <Dialog onClose={onClose} open={props.isModalOpen}>
      <DialogTitle>Change investment program</DialogTitle>
      <DialogContent dividers>
        <Alert severity='warning'>
          <Typography color='rgba(0,0,0,0.6)' mb={2} variant='body2'>
            You&apos;re about to change this plans&apos; investment program.
            This could mean re-defaulting a saver&apos;s investment election to
            a new QDIA.
          </Typography>
          <Typography color='rgba(0,0,0,0.6)' variant='body2'>
            Submitting the below may impact savers&apos; investment elections in
            the plan.
          </Typography>
        </Alert>
        <Typography mb={2} mt={2} variant='body1'>
          Please confirm the below changes.
        </Typography>
        <Divider />
        <Typography mt={2}>
          <strong>Plan Name:</strong> {props.planName}
        </Typography>
        <Typography mb={2}>
          <strong>Plan ID:</strong> {props.planId}
        </Typography>
        <Grid alignItems='stretch' container>
          <Grid item xs={5.5}>
            <StyledBox>
              <Typography variant='subtitle2'>Current Program</Typography>
              {props.currentProgramId ? (
                <>
                  <Typography variant='body1'>
                    {props.currentProgramName}
                  </Typography>
                  <Typography variant='body2'>
                    ID: {props.currentProgramId}
                  </Typography>
                </>
              ) : (
                <Typography variant='body1'>
                  No program associated with this plan
                </Typography>
              )}
            </StyledBox>
          </Grid>
          <Grid alignContent='center' item textAlign='center' xs={1}>
            <ArrowForward />
          </Grid>
          <Grid item xs={5.5}>
            <StyledBox>
              <Typography variant='subtitle2'>New Program</Typography>
              <Typography variant='body1'>{props.newProgramName}</Typography>
              <Typography variant='body2'>ID: {props.newProgramId}</Typography>
            </StyledBox>
          </Grid>
        </Grid>
        <Typography mt={2} variant='subtitle2'>
          Re-enrollment method
        </Typography>
        <Stack alignItems='center' direction='row' spacing={2}>
          {props.reEnrollmentType === 'full' ? (
            <ReEnrollmentFull fontSize='large' />
          ) : (
            <ReEnrollmentNone fontSize='large' />
          )}
          <Stack>
            <Typography variant='subtitle1'>
              {props.reEnrollmentType === 'full'
                ? 'Full Re-enrollment'
                : 'No Re-enrollment'}
            </Typography>
            <Typography variant='body2'>
              {props.reEnrollmentType === 'full'
                ? 'Re-enroll all savers in the plan in the new program`s QDIA'
                : 'Savers stay in their current investment elections'}
            </Typography>
          </Stack>
        </Stack>
        <Stack mt={3} spacing={3}>
          <Stack>
            <Typography variant='subtitle2'>Effective Date</Typography>
            <Typography variant='body1'>
              {formatters.formatFromIsoDateCustom(
                props.effectiveDate,
                'MM/DD/YYYY'
              )}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant='subtitle2'>Note</Typography>
            {props.effectiveDateNote?.startsWith('http') ? (
              <Link
                href={props.effectiveDateNote}
                rel='noopener noreferrer'
                target='_blank'>
                {props.effectiveDateNote.split('/').pop()}
              </Link>
            ) : (
              <Typography variant='body1'>{props.effectiveDateNote}</Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        {props.isForApprove ? (
          <Stack direction='row' justifyContent='space-between' width='100%'>
            <Button
              disabled={
                props.isLoading ||
                cancelProgramChange.isLoading ||
                approveOrRejectProgramChange.isLoading
              }
              onClick={onClose}>
              Close
            </Button>

            <AccessControl
              requires={[FeatureLevelPermissions.WRITE_INVESTMENTS_MODIFY]}>
              {props.status === 'approved' &&
              isAfter(startOfDay(props.effectiveDate), startOfToday()) ? (
                <LoadingButton
                  color='error'
                  loading={cancelProgramChange.isLoading}
                  onClick={onCancelSubmit}>
                  Cancel
                </LoadingButton>
              ) : props.status === 'pending' && props.isAnotherApprover ? (
                <Stack direction='row' spacing={1}>
                  <LoadingButton
                    color='error'
                    loading={approveOrRejectProgramChange.isLoading}
                    onClick={onRejectSubmit}>
                    Reject
                  </LoadingButton>
                  <LoadingButton
                    loading={approveOrRejectProgramChange.isLoading}
                    onClick={onApproveSubmit}
                    variant='contained'>
                    Approve & Submit
                  </LoadingButton>
                </Stack>
              ) : null}
            </AccessControl>
          </Stack>
        ) : (
          <>
            <Button disabled={props.isLoading} onClick={onClose}>
              Close
            </Button>
            <LoadingButton
              loading={props.isLoading}
              onClick={props.onForApprovalSubmit}
              variant='contained'>
              Submit for Approval
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

ReEnrollmentConfirmModal.displayName = 'ReEnrollmentConfirmModal';
